import { offlineDb, type CheckInDataset, type CheckInEntry, type OfflineDb, type QueuedCheckIn } from '@/lib/offline/offlineDb'

/**
 * The device's check-in data: each event's downloaded check-in list, and
 * check-ins made here that the server hasn't confirmed yet. Every write is
 * durable before it returns.
 */
export interface CheckInStore {
  dataset(userId: string, eventId: string): Promise<CheckInDataset | undefined>
  datasets(userId: string): Promise<CheckInDataset[]>
  saveDataset(dataset: CheckInDataset): Promise<void>
  removeDataset(userId: string, eventId: string): Promise<void>
  /** A walk-up registered here, so search and later decisions on this device know them. */
  addEntry(userId: string, eventId: string, entry: CheckInEntry): Promise<void>
  /** Reflect a check-in in the downloaded list, so later decisions on this device see it. */
  recordInDataset(userId: string, eventId: string, registrationId: string, at: string): Promise<void>
  /** The server says this person is already in: make sure the list agrees. */
  markKnownCheckedIn(userId: string, eventId: string, registrationId: string, firstAt: string | null): Promise<void>
  setMode(userId: string, eventId: string, mode: CheckInDataset['mode']): Promise<void>
  add(checkIn: Omit<QueuedCheckIn, 'status' | 'attempts' | 'nextAttemptAt' | 'lastError'>): Promise<void>
  forEvent(userId: string, eventId: string): Promise<QueuedCheckIn[]>
  counts(userId: string): Promise<{ waiting: number; needsAttention: number }>
  due(userId: string, now: Date): Promise<QueuedCheckIn[]>
  needingAttention(userId: string): Promise<QueuedCheckIn[]>
  markSyncing(id: string): Promise<void>
  markSynced(id: string): Promise<void>
  markRetry(id: string, error: string, nextAttemptAt: Date): Promise<void>
  markNeedsAttention(id: string, error: string): Promise<void>
  retry(id: string): Promise<void>
  discard(id: string): Promise<void>
  recoverInterrupted(userId: string): Promise<number>
  /** Check-in lists hold personal data: they leave the device with the person who signed out. */
  clearDatasets(userId: string): Promise<void>
}

export function createCheckInStore(db: OfflineDb = offlineDb): CheckInStore {
  const queue = db.checkIns
  const lists = db.checkInDatasets
  async function updateEntry(userId: string, eventId: string, registrationId: string, change: (entry: CheckInEntry) => CheckInEntry) {
    await db.transaction('rw', 'checkInDatasets', async () => {
      const dataset = await lists.get([userId, eventId])
      if (!dataset) return
      await lists.put({ ...dataset, entries: dataset.entries.map((entry) => (entry.registrationId === registrationId ? change(entry) : entry)) })
    })
  }
  return {
    dataset: (userId, eventId) => lists.get([userId, eventId]),
    datasets: (userId) => lists.where('userId').equals(userId).toArray(),
    async saveDataset(dataset) { await lists.put(dataset) },
    async addEntry(userId, eventId, entry) {
      await db.transaction('rw', 'checkInDatasets', async () => {
        const dataset = await lists.get([userId, eventId])
        if (dataset) await lists.put({ ...dataset, entries: [...dataset.entries.filter((existing) => existing.registrationId !== entry.registrationId), entry] })
      })
    },
    async removeDataset(userId, eventId) { await lists.where('[userId+eventId]').equals([userId, eventId]).delete() },
    recordInDataset: (userId, eventId, registrationId, at) => updateEntry(userId, eventId, registrationId, (entry) => ({
      ...entry,
      checkInCount: entry.checkInCount + 1,
      firstCheckedInAt: entry.firstCheckedInAt ?? at,
      lastCheckedInAt: at,
    })),
    markKnownCheckedIn: (userId, eventId, registrationId, firstAt) => updateEntry(userId, eventId, registrationId, (entry) => ({
      ...entry,
      checkInCount: Math.max(entry.checkInCount, 1),
      firstCheckedInAt: entry.firstCheckedInAt ?? firstAt,
      lastCheckedInAt: entry.lastCheckedInAt ?? firstAt,
    })),
    async setMode(userId, eventId, mode) {
      const dataset = await lists.get([userId, eventId])
      if (dataset) await lists.put({ ...dataset, mode })
    },
    async add(checkIn) {
      await queue.add({ ...checkIn, status: 'pending', attempts: 0, nextAttemptAt: null, lastError: null })
    },
    forEvent: (userId, eventId) => queue.where('[userId+eventId]').equals([userId, eventId]).toArray(),
    async counts(userId) {
      const [pending, syncing, error] = await Promise.all((['pending', 'syncing', 'error'] as const).map((status) => queue.where('[userId+status]').equals([userId, status]).count()))
      return { waiting: pending! + syncing!, needsAttention: error! }
    },
    async due(userId, now) {
      const pending = await queue.where('[userId+status]').equals([userId, 'pending']).toArray()
      return pending
        .filter((record) => !record.nextAttemptAt || new Date(record.nextAttemptAt) <= now)
        .sort((a, b) => a.checkedInAt.localeCompare(b.checkedInAt))
    },
    needingAttention: (userId) => queue.where('[userId+status]').equals([userId, 'error']).toArray(),
    async markSyncing(id) {
      await queue.where('clientCheckInId').equals(id).modify((record) => { record.status = 'syncing'; record.attempts += 1 })
    },
    async markSynced(id) { await queue.delete(id) },
    async markRetry(id, error, nextAttemptAt) { await queue.update(id, { status: 'pending', lastError: error, nextAttemptAt: nextAttemptAt.toISOString() }) },
    async markNeedsAttention(id, error) { await queue.update(id, { status: 'error', lastError: error, nextAttemptAt: null }) },
    async retry(id) { await queue.update(id, { status: 'pending', nextAttemptAt: null }) },
    async discard(id) { await queue.delete(id) },
    async recoverInterrupted(userId) {
      return queue.where('[userId+status]').equals([userId, 'syncing']).modify({ status: 'pending' })
    },
    async clearDatasets(userId) { await lists.where('userId').equals(userId).delete() },
  }
}

export const checkInStore = createCheckInStore()
